import { useContext } from 'react';

import { JourneyCharacteristicType, JourneyType, ProductCharacteristicType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';

import { FaiType, FaiTypeWithEligibility } from '@app-types/plan';

import { HomeZones } from '@helpers/eligibility';
import { paths } from '@helpers/path';
import { getNeededElementGencodes } from '@helpers/product';

interface UseAddToCartProps {
    plans: Record<string, FaiTypeWithEligibility>;
    idPanierCorbis: string | undefined;
    homeZone: HomeZones | undefined;
    showUpsellModal: (
        currentPlan: FaiType,
        onAddToCart: (gencode: string, autoAdd?: string[]) => Promise<void>,
        autoAdd?: string[],
    ) => boolean;
}

export function useAddToCart({ plans, idPanierCorbis, homeZone, showUpsellModal }: UseAddToCartProps) {
    const { addProductsToCartMutation, addCharacteristicToJourneyMutation } = useContext(CartContext);

    const addToCart = async (gencode: string, autoAdd?: string[], xgboxHomeZone?: string) => {
        if (idPanierCorbis) {
            await addCharacteristicToJourneyMutation.mutateAsync({
                journey: JourneyType.FAI_ACQUISITION,
                characteristic: { key: JourneyCharacteristicType.FAI_ORDER_CART_ID, value: idPanierCorbis },
            });
        }

        const productCharacteristics = xgboxHomeZone
            ? { [gencode]: [{ key: ProductCharacteristicType.HOME_ZONE, value: xgboxHomeZone }] }
            : undefined;

        await addProductsToCartMutation.mutateAsync({
            journey: JourneyType.FAI_ACQUISITION,
            gencodes: autoAdd ? [gencode, ...autoAdd] : [gencode],
            productCharacteristics,
        });
        window.location.assign(paths.cart);
    };

    const onAddToCart = async (gencode: string) => {
        const currentPlan = Object.values(plans).find((plan) => plan.gencode === gencode);

        if (!currentPlan) {
            await addToCart(gencode);
            return;
        }

        const autoAdd = getNeededElementGencodes(currentPlan);
        const xgboxHomeZone = currentPlan.rangeNg === 'xgbox' ? homeZone?.[currentPlan.technology] : undefined;

        // Try to show upsell modal, if it returns false, proceed with normal add to cart
        const isUpsellShown = showUpsellModal(currentPlan, (g, a) => addToCart(g, a, xgboxHomeZone), autoAdd);

        if (!isUpsellShown) {
            await addToCart(gencode, autoAdd, xgboxHomeZone);
        }
    };

    return {
        onAddToCart,
    };
}
